import { AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subscription } from 'rxjs';
import { DbcModel } from '../../model/dbc-model';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DbcService } from '../../service/dbc.service';
import { FormBuilder, FormGroup, Validators, FormControl, AbstractControl } from '@angular/forms';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { InMemoryCacheService, MessageType, UtilsService } from 'src/app/common';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProductCarouselComponent } from '../product-carousel/product-carousel.component';
import { FormFieldConfig, FieldType } from '../../model/contact-form-field.model';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { hasBusinessCardFeature, hasBusinessCardStandardFeatures, isBusinessCardStarterPlan } from '../../utils/business-card-plan-tier.util';

import { ActionButtonsComponent, ActionButton } from 'src/app/components/shared/action-buttons/action-buttons.component';
import { ConfirmationDialogComponent } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'gig-digital-business-card-landing',
    standalone: true,
    imports: [
        CommonModule,
        DialogModule,
        ButtonModule,
        InputTextModule,
        ReactiveFormsModule,
        FormsModule,
        DropdownModule,
        ToastModule,
        ConfirmDialogModule,
        ProductCarouselComponent,
        ActionButtonsComponent,
        ConfirmationDialogComponent,
        InputTextareaModule
    ],
    providers: [MessageService, ConfirmationService],
    templateUrl: './digital-business-card-landing.component.html',
    styleUrl: './digital-business-card-landing.component.scss'
})
export class DigitalBusinessCardLandingComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() previewMode: boolean = false;
    @Input() card: DbcModel | null = null;
    @Input() templateName: string = '';

    @ViewChild('default') default!: TemplateRef<any>;
    @ViewChild('template1') template1!: TemplateRef<any>;
    @ViewChild('template2') template2!: TemplateRef<any>;
    @ViewChild('template3') template3!: TemplateRef<any>;
    @ViewChild('template4') template4!: TemplateRef<any>;
    @ViewChild('template5') template5!: TemplateRef<any>;
    @ViewChild('template6') template6!: TemplateRef<any>;
    @ViewChild('template7') template7!: TemplateRef<any>;
    @ViewChild('template8') template8!: TemplateRef<any>;
    @ViewChild('template9') template9!: TemplateRef<any>;
    @ViewChild('template10') template10!: TemplateRef<any>;
    @ViewChild('template11') template11!: TemplateRef<any>;
    @ViewChild('template12') template12!: TemplateRef<any>;
    @ViewChild('template13') template13!: TemplateRef<any>;
    @ViewChild('template14') template14!: TemplateRef<any>;
    @ViewChild('template15') template15!: TemplateRef<any>;
    @ViewChild('template16') template16!: TemplateRef<any>;
    @ViewChild('template17') template17!: TemplateRef<any>;
    @ViewChild('template18') template18!: TemplateRef<any>;
    @ViewChild('template19') template19!: TemplateRef<any>;

    visibleContactModal: boolean = false;
    visibleCustomContactModal: boolean = false;
    addContactFormValue!: FormGroup;
    addContactSubmitting: boolean = false;

    selectedCustomFormName: string = 'Custom Contact Form';
    selectedCustomForm: FormFieldConfig[] = [];
    selectedCustomFormIndex: number | null = null;
    selectedCustomFormId = '';
    fieldConfigs: FormFieldConfig[] = [];
    customContactForm!: FormGroup;

    loading = true;
    notFound = false;

    previewImage: string | null = null;
    selectedTemplateRef: TemplateRef<any> | null = null;

    private formSub?: Subscription;

    // Action buttons for save contact
    actionButtons: ActionButton[] = [
        {
            label: 'Cancel',
            type: 'cancel',
            icon: 'pi pi-times mr-1',
            iconPos: 'left',
            outlined: true,
        },
        {
            label: 'Save',
            icon: 'pi pi-check mr-1',
            iconPos: 'left',
            type: 'confirm',
            disabled: true
        }
    ];

    public phoneTypes = [
        { label: 'Mobile', value: 'Mobile' },
        { label: 'Home', value: 'Home' },
        { label: 'Work', value: 'Work' },
        { label: 'Other', value: 'Other' }
    ];

    public dbcProductName = '';

    private route = inject(ActivatedRoute);
    private http = inject(HttpClient);
    private cdr = inject(ChangeDetectorRef);
    private dbc = inject(DbcService);
    private fb = inject(FormBuilder);
    private utils = inject(UtilsService);
    public cache = inject(InMemoryCacheService);
    // private confirmationService = inject(ConfirmationService);
    private router = inject(Router);

    get isStandardProduct(): boolean {
        return hasBusinessCardStandardFeatures(this.dbcProductName) || hasBusinessCardStandardFeatures(this.card?.planName);
    }

    get isStarterProduct(): boolean {
        return isBusinessCardStarterPlan(this.dbcProductName) || isBusinessCardStarterPlan(this.card?.planName);
    }

    get canAccessLeadCapture(): boolean {
        // A public card's entitlement belongs to its owning subscription, never
        // to the plan of a signed-in visitor viewing that card.
        const planName = this.card?.planName ?? this.dbcProductName;
        return hasBusinessCardFeature(planName, 'leadCapture');
    }

    get userViewMode(): boolean {
        const userView = this.route.snapshot.queryParamMap.get('userView');
        return userView === '1';
    }

    constructor(private messageService: MessageService) {}

    async ngOnInit(): Promise<void> {

        const userName = this.route.snapshot.paramMap.get('username');

        if(this.previewMode){
            await this.checkPlan();
        }

        this.initAddContactFormValue();

        if (!this.previewMode && !this.card) {
            try {

                if (!userName) {
                    this.notFound = true;
                    return;
                }

                this.card = await this.fetchBusinessCard(userName);

                if (!this.card) {
                    this.notFound = true;
                }
            } catch (error) {
                this.utils.handleApiError(error, 'Error fetching digital business card');
                this.notFound = true;
            } finally {
                this.loading = false;
            }
        } else if (this.previewMode) {
            this.loading = false;
        }
    }

    public ngAfterViewInit(): void {
        this.updateSelectedTemplate();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['templateName'] || this.templateName) {
            this.updateSelectedTemplate();
        }
    }

    public ngOnDestroy(): void {
        this.formSub?.unsubscribe();
    }

    public onButtonClick(e: any): void {
        if (e.type === 'cancel') {
            this.visibleContactModal = false;
        } else if (e.type === 'confirm') {
            this.addContactForm(this.addContactFormValue.value);
        }
    }

    async checkPlan() {
        if (!this.cache?.billingProducts) {
            await this.cache.getBillingProducts();
        }
        if(this.cache?.billingProducts){
            this.dbcProductName = this.cache?.billingProducts?.find(product => product?.feature === 'businessCard')?.name || '';
        }
    }

    initAddContactFormValue(){
        this.addContactFormValue = this.fb.group({
            id: [this.card?.id],
            projectId: [this.card?.projectId],
            name: ['', Validators.required],
            number: [''],
            type: ['Mobile', Validators.required],
            email: ['', Validators.email],
            organization: ['']
        });

        this.subscribeToFormChanges();
    }

    private subscribeToFormChanges() {
        if (this.formSub) {
            this.formSub.unsubscribe(); // ✅ prevent duplicate subscriptions
        }

       this.formSub = this.addContactFormValue.valueChanges.subscribe(() => {
            const number = this.addContactFormValue.get('number')?.value;
            const email = this.addContactFormValue.get('email')?.value;

            this.actionButtons[1].disabled =
                this.addContactFormValue.invalid || (!number && !email);
        });
    }

    // Function to check if either field touched AND has value
    isPhoneOrEmailTouchedAndHasValue(): boolean {
        const numberControl = this.addContactFormValue.get('number');
        const emailControl = this.addContactFormValue.get('email');

        const numberTouched = numberControl?.touched;
        const emailTouched = emailControl?.touched;

        const numberHasValue = !!numberControl?.value;
        const emailHasValue = !!emailControl?.value;

        // Returns true if at least one touched field has a value
        return (numberTouched || emailTouched) && (!numberHasValue && !emailHasValue);
    }

    getSocialIcon(platform: string): string {
        const platformMap: {[key: string]: string} = {
            'twitter': 'twitter',
            'x': 'twitter',
            'linkedin': 'linkedin',
            'facebook': 'facebook',
            'instagram': 'instagram',
            'github': 'github',
            'youtube': 'youtube',
            'whatsapp': 'whatsapp',
            'telegram': 'telegram',
            'reddit': 'reddit',
            'tiktok': 'tiktok',
            'twitch': 'twitch',
            'snapchat': 'snapchat',
            'pinterest': 'pinterest',
            'tumblr': 'tumblr',
            'flickr': 'flickr',
            'medium': 'medium',
            'behance': 'behance',
            'discord': 'discord',
            'slack': 'slack',
            'stack-overflow': 'stackoverflow',
            'stackoverflow': 'stackoverflow',
            'vimeo': 'vimeo',
            'soundcloud': 'soundcloud',
            'spotify': 'spotify',
            // Add more mappings as needed
        };

        return platformMap[platform.toLowerCase()] || 'globe';
    }

    getTemplateByName(name: string): TemplateRef<any> | null {
        const map: Record<string, TemplateRef<any>> = {
            default: this.default,
            template1: this.template1,
            template2: this.template2,
            template3: this.template3,
            template4: this.template4,
            template5: this.template5,
            template6: this.template6,
            template7: this.template7,
            template8: this.template8,
            template9: this.template9,
            template10: this.template10,
            template11: this.template11,
            template12: this.template12,
            template13: this.template13,
            template14: this.template14,
            template15: this.template15,
            template16: this.template16,
            template17: this.template17,
            template18: this.template18,
            template19: this.template19,
        };
        return map[name] || null;
    }

    downloadVCard(): void {
        const cardId = this.card?.id;
        if(cardId){
            this.dbc.trackSaveContact(cardId).subscribe();
        }
        if (this.card?.vcf) {
            window.open(this.card.vcf, '_blank');
        }
    }

    showPreview(imageUrl: string, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.previewImage = imageUrl;
        document.body.style.overflow = 'hidden';
    }

    closePreview(event: Event): void {
        if (
            event.target === event.currentTarget ||
            (event.target as HTMLElement).classList.contains('close-preview')
        ) {
            this.previewImage = null;
            document.body.style.overflow = '';
        }
    }

    getInitials(firstName: string, lastName: string): string {
        const firstInitial = firstName ? firstName.charAt(0) : '';
        const lastInitial = lastName ? lastName.charAt(0) : '';

        return (firstInitial + lastInitial).toUpperCase();
    }

    getContrastColor(hex: string): string {
        if (!hex) return '#fff';
        hex = hex.replace('#', '');
        if (hex.length === 3) {
            hex = hex.split('').map(h => h + h).join('');
        }
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 128 ? '#000' : '#fff';
    }

    generateGradient(baseHex: string | null | undefined): string {
        const fallback = '#666666';
        const primary = (baseHex && typeof baseHex === 'string' && baseHex.trim().length) ? baseHex : fallback;
        const safePrimary = primary.startsWith('#') ? primary : ('#' + primary);
        let secondColor: string;
        try {
            secondColor = this.adjustColor(safePrimary, -40);
        } catch {
            secondColor = fallback;
        }
        return `linear-gradient(135deg, ${safePrimary}, ${secondColor})`;
    }

    hexToRgba(hex: string | null | undefined, alpha: number): string {
        const fallback = (a: number) => `rgba(102,102,102,${a})`;
        if (!hex || typeof hex !== 'string') return fallback(alpha);
        hex = hex.trim();
        if (!hex) return fallback(alpha);
        hex = hex.replace('#', '');
        // Accept 3 or 6 hex digits only
        if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(hex)) return fallback(alpha);
        if (hex.length === 3) {
            hex = hex.split('').map(c => c + c).join('');
        }
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    async showDialogContactModal(cardId: string): Promise<void> {
        if (cardId) {
            this.visibleContactModal = true;
            this.actionButtons[1].disabled = true;
            try {
                // fire-and-forget, wait for completion
                await firstValueFrom(this.dbc.trackAddContactClicked(cardId));
            } catch {
                // ignore tracking errors
            }
        }
    }

    async showCustomDialogContactModal(cardId: string, customForm: any): Promise<void> {
        if (cardId) {
            this.visibleCustomContactModal = true;
            this.selectedCustomFormName = customForm?.name || 'Custom Contact Form';

            this.selectedCustomFormId = customForm.formId ?? '';
            this.selectedCustomForm = customForm.customFields ?? [];

            this.buildFormFromFields(this.selectedCustomForm);

            try {
                // fire-and-forget, wait for completion
                await firstValueFrom(this.dbc.trackAddContactClicked(cardId));
            } catch {
                // ignore tracking errors
            }
        }
    }

    private buildFormFromFields(fields: FormFieldConfig[]): void {
        this.fieldConfigs = fields.map(field => ({
            name: field.name,
            label: field.label,
            placeholder: field.placeholder || '',
            required: field.required === true || field.required === 'true',
            type: field.type as FieldType,
            options:
                field.type === 'select' || field.type === 'dropdown'
                ? field.options ?? []
                : undefined,
            pattern: field.pattern,
        }));
        this.utils.logger.info("[buildFormFromFields] ", this.fieldConfigs);
        this.buildForm(this.fieldConfigs);
    }

    private buildForm(configs: FormFieldConfig[]): void {
        const group: { [key: string]: FormControl } = {};

            for (const field of configs) {
                const validators = [];

                if (field.required) validators.push(Validators.required);
                if (field.type === 'email') validators.push(Validators.email);
                if (field.pattern) validators.push(Validators.pattern(field.pattern));

            group[field.name] = new FormControl(null, validators);
        }

        this.customContactForm = this.fb.group(group);
    }

    public async addContactForm(card: Record<string, unknown>): Promise<void> {
        if (this.addContactSubmitting) {
            return;
        }

        this.utils.logger.info("[addContactForm] ", card);

        this.addContactSubmitting = true;

        const cardId = this.card?.id ?? '';

        const values = {
            name: card?.name ?? '',
            phone: card?.number
                ? `${card.number}${card?.type ? ` (${card.type})` : ''}`
                : '',
            email: card?.email ?? '',
            organization: card?.organization ?? ''        };

        this.cache.loading = true;
        this.actionButtons.forEach(button => button.disabled = true);
        try {
            const res = (await this.dbc.addContactForm(cardId, values, null, 'default-contact')) as { id?: string };

            if (res?.id) {
                try {
                    await firstValueFrom(this.dbc.trackAddContactSubmitted(cardId));
                } catch { /* analytics must not fail the saved submission */ }
                this.utils.logger.info("Contact form added successfully:", res.id);
                this.messageService.add({ severity: 'success', detail: 'Contact form added successfully' });
                this.initAddContactFormValue();
                this.visibleContactModal = false;
            } else {
                this.messageService.add({ severity: 'danger', detail: 'No response ID. Contact form may not have been saved' });
            }
        } catch (err) {
            this.utils.handleApiError(err, 'Error while adding contact form');
        } finally {
            this.addContactSubmitting = false;
            this.cache.loading = false;
            this.actionButtons[0].disabled = false;
            this.actionButtons[1].disabled = !this.canSubmitDefaultContactForm();
        }
    }

    public async addCustomContactForm(customForm: Record<string, unknown>): Promise<void> {
        this.utils.logger.info("[addCustomContactForm] ", customForm);

        if (!customForm || this.addContactSubmitting) return;

        this.addContactSubmitting = true;
        const cardId = this.card?.id ?? '';
        this.utils.logger.info("[addCustomContactForm values] ", customForm);
        this.cache.loading = true;
        try {
            const normalizedValues = this.normalizeCustomFormValues(customForm);
            const res = (await this.dbc.addContactForm(cardId, normalizedValues, this.selectedCustomFormId, 'custom-form')) as { id?: string };

            if (res?.id) {
                try {
                    await firstValueFrom(this.dbc.trackAddContactSubmitted(cardId));
                } catch { /* analytics must not fail the saved submission */ }
                this.utils.logger.info("Custom contact form added successfully:", res.id);
                this.messageService.add({ severity: 'success', detail: 'Custom contact form added successfully' });
                this.customContactForm.reset();
                this.visibleCustomContactModal = false;
            } else {
                this.messageService.add({ severity: 'danger', detail: 'No response ID. Custom contact form may not have been saved' });
            }
        } catch (err) {
            this.utils.handleApiError(err, 'Error while adding custom contact form');
        } finally {
            this.addContactSubmitting = false;
            this.cache.loading = false;
        }
    }

    private normalizeCustomFormValues(values: Record<string, unknown>): Record<string, unknown> {
        const fieldTypes = new Map(
            this.selectedCustomForm.map((field: FormFieldConfig) => [field.name, field.type])
        );

        return Object.entries(values).reduce<Record<string, unknown>>((normalized, [name, value]) => {
            if (fieldTypes.get(name) === 'number' && typeof value === 'string' && value.trim() !== '') {
                const numericValue = Number(value);
                normalized[name] = Number.isFinite(numericValue) ? numericValue : value;
            } else {
                normalized[name] = value;
            }
            return normalized;
        }, {});
    }

    hasPhoneNumber(card: any): boolean {
        return card?.phone?.some((p: any) => p.number?.trim());
    }

    hasSocialUrl(card: any): boolean {
        return card?.social?.some((s: any) => s.url?.trim());
    }

    hasLinkUrl(card: any): boolean {
        return card?.links?.some((l: any) => l.url?.trim());
    }

    private updateSelectedTemplate(): void {
        if (this.templateName) {
            this.selectedTemplateRef = this.getTemplateByName(this.templateName);
            this.cdr.detectChanges();
        }
    }

    private adjustColor(hex: string | null | undefined, amount: number): string {
        if (!hex || typeof hex !== 'string') return '#666666';
        hex = hex.trim();
        if (!hex) return '#666666';
        let usePound = false;
        if (hex.startsWith('#')) {
            hex = hex.slice(1);
            usePound = true;
        }
        // Validate hex length (3 or 6). If invalid, fallback.
        if (![3,6].includes(hex.length) || !/^[0-9a-fA-F]+$/.test(hex)) {
            return '#666666';
        }
        if (hex.length === 3) {
            hex = hex.split('').map(c => c + c).join('');
        }
        const num = parseInt(hex, 16);
        let r = (num >> 16) + amount;
        let g = ((num >> 8) & 0x00ff) + amount;
        let b = (num & 0x0000ff) + amount;
        r = Math.max(Math.min(255, r), 0);
        g = Math.max(Math.min(255, g), 0);
        b = Math.max(Math.min(255, b), 0);
        const result = (1 << 24) + (r << 16) + (g << 8) + b;
        return (usePound ? '#' : '') + result.toString(16).slice(1);
    }

    private async fetchBusinessCard(userName: string): Promise<DbcModel | null> {
        try {
            const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard/userName/${userName}`;
            const response = await firstValueFrom(this.http.get<DbcModel>(url));
            this.utils.logger.info("[fetchBusinessCard] ", response);
            if (response) {
                const cardId = response?.id;

                this.addContactFormValue.patchValue({
                    id: cardId
                })
                // Track view event on load
                if(cardId){
                    this.dbc.trackView(cardId).subscribe();
                }

                this.templateName = response?.template;
                setTimeout(() => {
                    this.updateSelectedTemplate();
                }, 0);
            }
            return response;
        } catch (error) {
            this.utils.handleApiError(error, 'Error fetching digital business card');
            return null;
        }
    }

    goBack(): void {
        this.router.navigate(['/dbc']);
    }

    private canSubmitDefaultContactForm(): boolean {
        const number = this.addContactFormValue?.get('number')?.value;
        const email = this.addContactFormValue?.get('email')?.value;
        return !!this.addContactFormValue && this.addContactFormValue.valid && (!!number || !!email);
    }
}
